const ACCENT = "text-[#C2410C]";

const FAQ = [
  {
    q: "How do I open a file?",
    a: (
      <>
        Tap <strong>Open</strong> on the home screen and pick a file from your
        device. You can also open files straight from other apps: in WhatsApp,
        Google Drive, or a file manager, tap the file and choose{" "}
        <strong>LayerPeek</strong> from the &quot;Open with&quot; list, or tap
        Share and pick LayerPeek in the share sheet. Files you have opened
        before appear in the recent files list on the home screen.
      </>
    ),
  },
  {
    q: "Which file types are supported?",
    a: (
      <>
        LayerPeek opens <strong>STL</strong>,{" "}
        <strong>OBJ</strong>, <strong>3MF</strong> (including Bambu Studio and
        OrcaSlicer project files, with their plates and colours), sliced{" "}
        <strong>.gcode.3mf</strong> files, <strong>G-code</strong>, and{" "}
        <strong>ZIP</strong> downloads that contain any of these. STEP files
        and binary G-code (.bgcode) are not supported yet; support for them is
        planned.
      </>
    ),
  },
  {
    q: "What does \"fits bed\" mean? Can I add my own printer?",
    a: (
      <>
        The bed fit check compares the size of your model with the build
        volume (width, depth, and height) of the printer you selected, and
        tells you whether the model fits on that printer. Pick your printer in <strong>Settings</strong>. If your printer
        is not in the list, add a custom printer and enter its build volume.
      </>
    ),
  },
  {
    q: "Why are print time and weight exact only for some files?",
    a: (
      <>
        A sliced file (G-code or .gcode.3mf) already contains the slicer&apos;s
        own figures for print time and filament used, so LayerPeek shows those
        values. An unsliced model (STL, OBJ, or a 3MF project) has no toolpath
        yet, so LayerPeek estimates weight and cost from the model&apos;s solid
        volume, your chosen material density, and your filament price. Real
        prints use infill and walls, so they usually weigh less than the solid
        estimate. Treat it as an upper bound, and slice the model for exact
        numbers.
      </>
    ),
  },
  {
    q: "Are there limits on file size?",
    a: (
      <>
        Files up to <strong>512 MB</strong> can be opened. Very large models
        are shown as a simplified preview so the view stays smooth on your
        phone, while dimensions, bed fit, mesh health, and volume are still
        calculated from the full model. Large files take longer to open on
        older devices.
      </>
    ),
  },
  {
    q: "How do I make a case for my board?",
    a: (
      <>
        On the home screen, tap <strong>Make something</strong> and choose{" "}
        <strong>Board case</strong>. Pick your board from the list (ESP32,
        Raspberry Pi, Arduino and more), then adjust the settings such as wall
        thickness, fit clearance, and standoff height.
        The preview updates as you change them. When it looks right, tap{" "}
        <strong>Save as</strong> and choose <strong>STL</strong> (one file for
        any slicer) or <strong>3MF</strong> (keeps the parts separate so each
        can have its own colour), then pick the folder to save it in. The case
        is built on your phone and nothing is uploaded. The same screen also
        has boxes with dividers, name tags, cable clips, and spacers.
      </>
    ),
  },
  {
    q: "What is the fit test?",
    a: (
      <>
        The fit test is a quick, low-filament version of a board case: a thin
        floor, the board supports, and a low wall. Turn on{" "}
        <strong>Fit test only</strong> in the case settings, save, and print it
        first. It takes a few minutes and a few grams of filament, and shows
        whether your board sits correctly on the supports.
        If it fits, turn the option off and print the full case. This matters
        most for boards sold as clones, whose sizes can vary.
      </>
    ),
  },
  {
    q: "My board isn't in the list",
    a: (
      <>
        Choose <strong>Custom board</strong> in the board list and type in your
        board&apos;s measurements. Use calipers and measure in millimetres,
        looking at the board from the side with the chips, long edge left to
        right:
        <ul className="mt-2 list-disc space-y-1 pl-6">
          <li>
            The length and width of the board itself, not the pins or the USB
            plug.
          </li>
          <li>
            Each mounting hole: from the bottom-left corner to the centre of the
            hole, across and up.
          </li>
          <li>
            The board thickness, the tallest part on top, and how far pins stick
            out underneath.
          </li>
          <li>
            Each port: which edge it is on, the distance from the corner to its
            centre, and the width and height of the opening.
          </li>
        </ul>
        <p className="mt-2">
          Tap <strong>How to measure</strong> in the app for the same steps,
          and print the fit test before the full case. If you would like a
          board added to the list, email us its name.
        </p>
      </>
    ),
  },
  {
    q: "How does Describe it / COSMO AI work and what does it cost?",
    a: (
      <>
        <strong>Describe it</strong> opens a design chat with COSMO AI. Tell it
        what you want to print, for example &quot;a case for my ESP32 with the
        USB on the left&quot;. COSMO AI picks a template, fills in the sizes,
        and shows a preview, and you can open the result in the designer to
        change anything by hand. You can also tap <strong>Ask COSMO</strong>{" "}
        while editing a template to ask for changes.
        <ul className="mt-2 list-disc space-y-1 pl-6">
          <li>
            Each message you send costs <strong>1 credit</strong>. The price is
            shown on the send button.
          </li>
          <li>
            Tapping a suggested answer that only changes a setting is applied
            on your phone and is free.
          </li>
          <li>
            If something goes wrong, or an answer comes back unusable, you are
            not charged.
          </li>
          <li>
            Credits are shared with other Cosmovex apps that have COSMO AI on
            the same device, and more can be bought in the app through Google
            Play. No sign-in is needed.
          </li>
          <li>
            COSMO AI needs an internet connection. AI can get sizes wrong, so
            check them against your part and print the fit test first.
          </li>
          <li>
            <strong>Every template works without AI.</strong> You never need
            credits to make a part.
          </li>
        </ul>
        <p className="mt-2">
          If an answer is wrong or offensive, tap{" "}
          <strong>Report this answer</strong> under it. See the{" "}
          <a
            href="/apps/layerpeek/privacy-policy"
            className="text-[#0F6BBF] underline"
          >
            Privacy Policy
          </a>{" "}
          for what is sent when you use COSMO AI.
        </p>
      </>
    ),
  },
  {
    q: "Can I make figurines or toys?",
    a: (
      <>
        Not yet. Making models from a photo or a description, such as figurines
        and toys, is coming soon. Tap the <strong>Coming soon</strong> card on
        the Make screen and choose to be notified, and LayerPeek will tell you
        in the app when it is ready. Today, Make covers board cases, boxes,
        name tags, cable clips, and spacers.
      </>
    ),
  },
  {
    q: "How do I delete my COSMO data?",
    a: (
      <>
        Open <strong>Settings &gt; COSMO Account</strong> and tap{" "}
        <strong>Delete COSMO Account</strong>. After you confirm, LayerPeek
        opens a prefilled email with your anonymous COSMO account ID. Send it,
        and we delete your account. Nothing is deleted until you send the
        email. Deletion covers your COSMO credits in every Cosmovex app with
        COSMO AI, including credits you paid for, which are not refundable.
        Your LayerPeek Pro purchase is not affected. The design chat itself is
        stored only on your phone and is cleared when you leave it.
      </>
    ),
  },
  {
    q: "Does LayerPeek work offline?",
    a: (
      <>
        Yes. Opening, viewing, measuring, estimating, and making parts from
        templates all run on your device without an internet connection. Files
        stored only in the cloud (for example in Google Drive) need a
        connection for that app to download them first. Ads, purchases, and
        COSMO AI also need a connection.
      </>
    ),
  },
  {
    q: "Why does the app show ads? How do I remove them?",
    a: (
      <>
        LayerPeek is free and ads support its development. A one-time{" "}
        <strong>Pro</strong> purchase in the app removes ads. It is a single
        payment through Google Play, not a subscription. You can review your ad
        consent choice at any time in{" "}
        <strong>Settings &gt; Ad Privacy Choices</strong>.
      </>
    ),
  },
  {
    q: "I bought Pro but still see ads. How do I restore my purchase?",
    a: (
      <>
        Make sure you are signed in to Google Play with the same Google account
        you used to buy Pro, then open <strong>Settings</strong> in LayerPeek
        and use the restore purchase option. Purchases are tied to your
        Google account, so Pro also comes back after reinstalling the app or
        moving to a new phone. If it still does not work, email us your Google
        Play order number (it starts with GPA.).
      </>
    ),
  },
  {
    q: "The app is crashing or a model won't open. What do I do?",
    a: (
      <>
        Close and reopen the app, and check that you have the latest version
        from Google Play. If a specific file will not open or looks wrong,
        email us and include:
        <ul className="mt-2 list-disc space-y-1 pl-6">
          <li>The file, if you are comfortable sharing it (or a link to it)</li>
          <li>
            Where the file came from (for example the slicer and version that
            exported it, or the website you downloaded it from)
          </li>
          <li>Your device model and Android version</li>
          <li>Your LayerPeek version (Settings &gt; About)</li>
          <li>What happened, and a screenshot if possible</li>
        </ul>
        <p className="mt-2">
          Sending us a file is optional and happens only by email, at your
          choice. The app never uploads files.
        </p>
      </>
    ),
  },
  {
    q: "Are my files private?",
    a: (
      <>
        Yes. Your files are opened and rendered on your device and never leave
        it, and parts you make are saved only where you choose. LayerPeek has
        no account and does not upload files or file names. The app sends
        anonymous usage events, crash reports, and ad data to Google services.
        If you use the optional COSMO AI design chat, the messages you type and
        the settings of the template you are working on are sent to COSMO AI.
        See the{" "}
        <a
          href="/apps/layerpeek/privacy-policy"
          className="text-[#0F6BBF] underline"
        >
          Privacy Policy
        </a>{" "}
        for details.
      </>
    ),
  },
];

export default function LayerPeekSupport() {
  return (
    <main className="min-h-screen bg-[#FBFAF6] text-[#26313B]">
      <section className="mx-auto max-w-3xl px-6 py-12">
        <header className="mb-8 text-center">
          <h1 className={`text-4xl font-extrabold tracking-tight ${ACCENT}`}>
            Support - LayerPeek 3D Print File Viewer
          </h1>
          <p className="mt-2 text-sm text-[#5B6B7C]">
            Help, troubleshooting, and contact details for LayerPeek.
          </p>
          <p className="mt-1 text-xs text-[#8A93A2]">
            Published by <span className="font-semibold">Cosmovex</span>
          </p>
        </header>

        <div className="space-y-8 leading-relaxed text-[#3D4B59]">
          <h2 className={`text-2xl font-bold ${ACCENT}`}>
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            {FAQ.map(({ q, a }) => (
              <div key={q}>
                <h3 className="text-lg font-semibold text-[#26313B]">{q}</h3>
                <div className="mt-1">{a}</div>
              </div>
            ))}
          </div>

          <h2 className={`text-2xl font-bold ${ACCENT}`}>Contact Support</h2>
          <p>
            For help, bug reports, or privacy-related questions, email{" "}
            <a
              href="mailto:trionapps@gmail.com"
              className="text-[#0F6BBF] underline"
            >
              trionapps@gmail.com
            </a>
            . We usually respond within 24-48 hours.
          </p>

          <h2 className={`text-2xl font-bold ${ACCENT}`}>Report a Bug</h2>
          <p>
            If something is not working as expected, include these details in
            your email:
          </p>
          <ul className="list-disc space-y-1 pl-6">
            <li>Your device model and Android OS version</li>
            <li>Your app version (found in Settings &gt; About)</li>
            <li>The file type you were opening (STL, OBJ, 3MF, G-code, ZIP)</li>
            <li>What you were trying to do</li>
            <li>The error or unexpected result</li>
            <li>Steps to reproduce the issue, if known</li>
          </ul>

          <h2 className={`text-2xl font-bold ${ACCENT}`}>Important Links</h2>
          <ul className="list-disc space-y-1 pl-6">
            <li>
              Privacy Policy:{" "}
              <a
                href="/apps/layerpeek/privacy-policy"
                className="text-[#0F6BBF] underline"
              >
                cosmovexapps.com/apps/layerpeek/privacy-policy
              </a>
            </li>
            <li>
              Support:{" "}
              <a
                href="/apps/layerpeek/support"
                className="text-[#0F6BBF] underline"
              >
                cosmovexapps.com/apps/layerpeek/support
              </a>
            </li>
          </ul>

          <h2 className={`text-2xl font-bold ${ACCENT}`}>App Information</h2>
          <ul className="list-disc space-y-1 pl-6">
            <li>
              <strong>App Name:</strong> LayerPeek 3D Print File Viewer
            </li>
            <li>
              <strong>Package:</strong> com.cosmovex.layerpeek
            </li>
            <li>
              <strong>Platform:</strong> Android
            </li>
            <li>
              <strong>Developer:</strong> Cosmovex
            </li>
            <li>
              <strong>Contact:</strong>{" "}
              <a
                href="mailto:trionapps@gmail.com"
                className="text-[#0F6BBF] underline"
              >
                trionapps@gmail.com
              </a>
            </li>
          </ul>
        </div>

        <footer className="mt-12 border-t border-[#E1DCCE] pt-4 text-sm text-[#8A93A2]">
          &copy; 2026 Cosmovex - All rights reserved.
        </footer>
      </section>
    </main>
  );
}
